import { QuoteStatus } from '@/types';
import type { Quote } from '@/types';

/**
 * Wie heeft deze offerte ondertekend?
 *
 * Het overzicht moet drie vragen kunnen beantwoorden zonder de offerte te
 * openen: heeft de klant getekend, hebben wij (UBO) getekend, en is daarmee
 * iedereen klaar. Die afleiding staat hier één keer, zodat de lijst, de kaarten
 * en het pipelinebord nooit een ander antwoord kunnen geven.
 */

export type SigningState = 'signed' | 'partial' | 'open';

export interface QuoteSigner {
  name: string;
  signed: boolean;
  signedAt: string | null;
}

export interface QuoteSignatureOverview {
  /** Valt er iets te melden? Bij een vers concept zonder handtekeningen niet. */
  relevant: boolean;
  /** Klantzijde: alle tekenbevoegden getekend, een deel, of nog niemand. */
  client: SigningState;
  clientSigned: number;
  clientTotal: number;
  clientSigners: QuoteSigner[];
  clientSignedAt: string | null;
  /** UBO-zijde: de medeondertekening is één handtekening, dus nooit 'partial'. */
  ubo: Exclude<SigningState, 'partial'>;
  uboName: string | null;
  uboSignedAt: string | null;
  /** Klant is klaar, de UBO-handtekening ontbreekt nog: dit vraagt actie van ons. */
  awaitingUbo: boolean;
  everyoneSigned: boolean;
}

/** Statussen waarin de klant de offerte hoort te kunnen tekenen. */
const SIGNABLE_STATUSES: string[] = [
  QuoteStatus.SENT,
  QuoteStatus.VIEWED,
  QuoteStatus.ACCEPTED,
  QuoteStatus.EXPIRED,
];

export function getSignatureOverview(quote: Quote): QuoteSignatureOverview {
  const summary = quote.signatureSummary;
  const clientTotal = summary?.recipientsTotal ?? 0;
  const clientSigned = summary?.recipientsSigned ?? 0;
  const uboSignedAt = summary?.senderSignedAt ?? null;
  const uboName = summary?.senderSignerName?.trim() || null;

  const clientSigners: QuoteSigner[] =
    summary?.recipientSigners?.map((signer) => ({
      name: signer.name?.trim() || 'Tekenbevoegde',
      signed: signer.signed,
      signedAt: signer.signedAt ?? null,
    })) ??
    // Oudere lijstreacties kennen alleen de namen; dan blijft alleen de telling over.
    (summary?.recipientNames ?? []).map((name, index) => ({
      name: name?.trim() || 'Tekenbevoegde',
      signed: index < clientSigned,
      signedAt: null,
    }));

  const clientAllSigned =
    clientTotal > 0 ? clientSigned >= clientTotal : Boolean(summary?.recipientSignedAt);
  const client: SigningState = clientAllSigned ? 'signed' : clientSigned > 0 ? 'partial' : 'open';
  const ubo = uboSignedAt ? 'signed' : 'open';

  const everyoneSigned = clientAllSigned && Boolean(uboSignedAt);
  const anySignature = clientSigned > 0 || Boolean(uboSignedAt);
  const relevant = anySignature || (clientTotal > 0 && SIGNABLE_STATUSES.includes(quote.status));

  return {
    relevant,
    client,
    clientSigned,
    clientTotal,
    clientSigners,
    clientSignedAt: summary?.recipientSignedAt ?? null,
    ubo,
    uboName,
    uboSignedAt,
    awaitingUbo: Boolean(summary?.countersignRequired),
    everyoneSigned,
  };
}

/** Heeft deze offerte een ondertekenstand om te tonen? */
export function hasSignatureStatus(quote: Quote): boolean {
  return getSignatureOverview(quote).relevant;
}

/** '12 sep 2026': een tekenmoment is een vaste datum, geen 'x dagen geleden'. */
export const formatSignedDate = (value: string) =>
  new Date(value).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });

const signedDate = (value: string | null) => (value ? formatSignedDate(value) : null);

/**
 * Wanneer is de offerte ondertekend? Op het moment dat de laatste tekenbevoegde
 * van de klant tekende: vanaf dan is de offerte geaccepteerd. De UBO-
 * medeondertekening volgt daarna en staat in de tooltip van de ondertekenstatus.
 */
export function getQuoteSignedAt(quote: Quote): string | null {
  const overview = getSignatureOverview(quote);
  if (overview.client !== 'signed') return null;
  if (overview.clientSignedAt) return overview.clientSignedAt;
  const signerDates = overview.clientSigners
    .map((signer) => signer.signedAt)
    .filter((value): value is string => Boolean(value))
    .sort();
  return signerDates.at(-1) ?? null;
}

/** Eén regel per tekenbevoegde: '✓ Jan de Vries · 12 sep 2026' of 'nog niet getekend'. */
export function describeSigner(signer: QuoteSigner): string {
  const date = signedDate(signer.signedAt);
  if (!signer.signed) return `${signer.name} — nog niet getekend`;
  return date ? `${signer.name} — getekend op ${date}` : `${signer.name} — getekend`;
}

export function describeUboSigner(overview: QuoteSignatureOverview): string {
  const name = overview.uboName || 'UBO-tekenaar onbekend';
  if (!overview.uboSignedAt) {
    return overview.awaitingUbo ? `${name} — moet nog tekenen` : `${name} — nog niet getekend`;
  }
  const date = signedDate(overview.uboSignedAt);
  return date ? `${name} — getekend op ${date}` : `${name} — getekend`;
}

/** Platte tekst met alle ondertekenaars, voor tooltips en `title`-attributen. */
export function describeSignatureOverview(overview: QuoteSignatureOverview): string {
  const client =
    overview.clientSigners.length > 0
      ? overview.clientSigners.map(describeSigner).join('; ')
      : 'geen tekenbevoegden ingesteld';
  return `Klant: ${client}. UBO: ${describeUboSigner(overview)}.`;
}

/**
 * In compacte weergave dragen het icoon en de kleur de stand, niet de tekst:
 * op een pipelinekaart passen twee volzinnen simpelweg niet naast elkaar.
 */
export function getClientSignatureLabel(overview: QuoteSignatureOverview, compact = false): string {
  if (overview.client === 'partial') {
    const progress = `${overview.clientSigned}/${overview.clientTotal}`;
    return compact ? `Klant ${progress}` : `Klant ${progress} getekend`;
  }
  if (overview.client === 'signed') {
    return compact ? 'Klant' : 'Klant getekend';
  }
  return compact ? 'Klant' : 'Klant nog niet getekend';
}

export function getUboSignatureLabel(overview: QuoteSignatureOverview, compact = false): string {
  if (compact) return 'UBO';
  if (overview.ubo === 'signed') return 'UBO getekend';
  return overview.awaitingUbo ? 'UBO moet nog tekenen' : 'UBO nog niet getekend';
}
